import React, { useState } from "react";
import hosys from "@/api/api";
import { DEFAULT_LIMITS as TEMP_LIMITS, EQUIPMENT_LABELS } from "@/components/services/temperatureService";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";

const EQUIPMENT_TYPES = [
  { value: "fridge", label: "Nevera" },
  { value: "freezer", label: "Congelador" },
  { value: "chamber", label: "Cambra" },
  { value: "prepared_food", label: "Menjar preparat" },
  { value: "fryer_oil", label: "Oli fregidora" },
  { value: "cold_reception", label: "Recepció freds" },
];

function EquipRow({ eq, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(eq.name);
  const [type, setType] = useState(eq.equipment_type);
  const [min, setMin] = useState(eq.min_limit ?? TEMP_LIMITS[eq.equipment_type]?.min ?? 0);
  const [max, setMax] = useState(eq.max_limit ?? TEMP_LIMITS[eq.equipment_type]?.max ?? 10);

  const handleSave = async () => {
    await onSave(eq.id, { name, equipment_type: type, min_limit: parseFloat(min), max_limit: parseFloat(max) });
    setEditing(false);
  };

  const handleTypeChange = (t) => {
    setType(t);
    setMin(TEMP_LIMITS[t]?.min ?? 0);
    setMax(TEMP_LIMITS[t]?.max ?? 10);
  };

  if (editing) {
    return (
      <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 space-y-3">
        <div className="flex gap-2 flex-wrap">
          {EQUIPMENT_TYPES.map(t => (
            <button key={t.value} onClick={() => handleTypeChange(t.value)}
              className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${type === t.value ? "bg-[#7B2D3F] text-white border-[#7B2D3F]" : "bg-white text-gray-600 border-gray-200"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="Nom de l'equip" className="text-sm" />
        <div className="flex items-center gap-3">
          <label className="text-xs text-gray-500 w-16">Mín (°C)</label>
          <Input type="number" step="0.5" value={min} onChange={e => setMin(e.target.value)} className="w-24 text-sm" />
          <label className="text-xs text-gray-500 w-16">Màx (°C)</label>
          <Input type="number" step="0.5" value={max} onChange={e => setMax(e.target.value)} className="w-24 text-sm" />
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-1">
            <Check className="w-3 h-3" /> Guardar
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
            <X className="w-3 h-3" /> Cancel·lar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 hover:border-gray-200 transition-all">
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 text-sm">{eq.name}</p>
        <p className="text-xs text-gray-400">{EQUIPMENT_LABELS[eq.equipment_type]} · {eq.min_limit}° a {eq.max_limit}°C</p>
      </div>
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(true)}>
        <Pencil className="w-3.5 h-3.5 text-gray-400" />
      </Button>
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onDelete(eq.id)}>
        <Trash2 className="w-3.5 h-3.5 text-red-400" />
      </Button>
    </div>
  );
}

export default function EquipmentManager({ venueId, equipments, onRefresh }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("fridge");
  const [newMin, setNewMin] = useState(TEMP_LIMITS["fridge"].min);
  const [newMax, setNewMax] = useState(TEMP_LIMITS["fridge"].max);
  const [saving, setSaving] = useState(false);

  const handleNewTypeChange = (t) => {
    setNewType(t);
    setNewMin(TEMP_LIMITS[t]?.min ?? 0);
    setNewMax(TEMP_LIMITS[t]?.max ?? 10);
  };

  const handleAdd = async () => {
    if (!newName) return;
    setSaving(true);
    await hosys.entities.EquipmentConfig.create({
      venue_id: venueId,
      name: newName,
      equipment_type: newType,
      min_limit: parseFloat(newMin),
      max_limit: parseFloat(newMax),
      active: true,
    });
    setNewName("");
    setAdding(false);
    setSaving(false);
    onRefresh();
  };

  const handleSave = async (id, data) => {
    await hosys.entities.EquipmentConfig.update(id, data);
    onRefresh();
  };

  const handleDelete = async (id) => {
    await hosys.entities.EquipmentConfig.delete(id);
    onRefresh();
  };

  return (
    <Card className="border-0 shadow-sm overflow-hidden">
      <div className="px-5 py-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900 text-sm">Equips configurats</h3>
          <p className="text-xs text-gray-400 mt-0.5">Edita els noms i límits de cada equip</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setAdding(true)} className="gap-1 text-xs">
          <Plus className="w-3 h-3" /> Nou equip
        </Button>
      </div>

      <div className="p-4 space-y-2">
        {adding && (
          <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 space-y-3 mb-3">
            <p className="text-xs font-semibold text-gray-700">Nou equip</p>
            <div className="flex gap-2 flex-wrap">
              {EQUIPMENT_TYPES.map(t => (
                <button key={t.value} onClick={() => handleNewTypeChange(t.value)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${newType === t.value ? "bg-[#7B2D3F] text-white border-[#7B2D3F]" : "bg-white text-gray-600 border-gray-200"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nom de l'equip (ex: Nevera cuina 1)" className="text-sm" />
            <div className="flex items-center gap-3">
              <label className="text-xs text-gray-500 w-16">Mín (°C)</label>
              <Input type="number" step="0.5" value={newMin} onChange={e => setNewMin(e.target.value)} className="w-24 text-sm" />
              <label className="text-xs text-gray-500 w-16">Màx (°C)</label>
              <Input type="number" step="0.5" value={newMax} onChange={e => setNewMax(e.target.value)} className="w-24 text-sm" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} disabled={!newName || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-1">
                <Check className="w-3 h-3" /> {saving ? "Guardant..." : "Afegir"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
                <X className="w-3 h-3" /> Cancel·lar
              </Button>
            </div>
          </div>
        )}

        {equipments.length === 0 && !adding && (
          <p className="text-sm text-gray-400 text-center py-4">No hi ha equips configurats. Afegeix-ne un!</p>
        )}

        {equipments.map(eq => (
          <EquipRow key={eq.id} eq={eq} onSave={handleSave} onDelete={handleDelete} />
        ))}
      </div>
    </Card>
  );
}